import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tabs, Tab, Typography, Grid, Button, Input, Tooltip } from '@mui/material';
import { useFetchProductos } from '@/shared/actions/erp';
import { useTableFilter, useTableServerSideFiltering } from '@/shared';
import { MRT_ColumnDef } from 'material-react-table';
import { GeneralTable } from '@/shared/components/ui';
import { IDataProducto } from '@/shared/interfaces';
import { exportExcel, mapSeriesToExcel } from '../function/exportarExcel';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { toast } from 'react-toastify';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

export default function GeneradorSeriesPage() {
    const [tabValue, setTabValue] = useState(0);
    const [cantidadesPorFila, setCantidadesPorFila] = useState<Record<string | number, number>>({});
    const lastSequenceRef = useRef<number>(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const { filterObject, columnFilters, setColumnFilters } = useTableServerSideFiltering();

    const {
        pagination,
        setPagination
    } = useTableFilter();
    const { pageIndex, pageSize } = pagination;

    const { data: dataProductos, isLoading: isLoadingProductos, isRefetching } = useFetchProductos({
        params: {
            page: pageIndex + 1,
            page_size: pageSize,
            contiene_serie: true,
            estado: true,
            ...filterObject,
        },
    })

    const {
        pagination: paginationSeries,
        setPagination: setPaginationSeries,
    } = useTableFilter();

    useEffect(() => {
        if (!dataProductos || isLoadingProductos) return
    }, [dataProductos])

    // Manejar cambio de cantidad - limpia las demás filas
    const handleCantidadChange = (productId: string | number, value: number) => {
        setCantidadesPorFila({ [productId]: value });
    };

    const columns = useMemo<MRT_ColumnDef<IDataProducto>[]>(
        () => [
            {
                accessorKey: "codigo",
                header: "Codigo",
                size: 50,
            },
            {
                accessorKey: "descripcion",
                header: "Descripción",
                size: 300,
            },
            {
                header: "CANTIDAD",
                size: 50,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ row }) => {
                    const productId = row.original.id_producto;
                    const cantidadActual = cantidadesPorFila[productId] || 0;
                    return (
                        <Input
                            placeholder='Cantidad'
                            type='number'
                            size='small'
                            value={cantidadActual}
                            onChange={(e) => handleCantidadChange(productId, parseInt(e.target.value) || 0)}
                        />
                    );
                },
            },
            {
                header: "ACCIONES",
                size: 50,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ row }) => {
                    return <Button size='small' variant='outlined' color='success' onClick={() => onGenerate(row.original)}>Generar</Button>
                },
            },
        ],
        [cantidadesPorFila]
    );

    const [seriesGeneradas, setSeriesGeneradas] = useState<string[]>([]);
    const onGenerate = (original: IDataProducto) => {
        const cantidad = cantidadesPorFila[original.id_producto] || 0;

        if (cantidad === 0) {
            toast.error("Debe ingresar una cantidad válida")
            return;
        }

        // Obtener 2 primeras letras del nombre
        const letras = original.descripcion.substring(0, 2).toUpperCase();

        // Obtener fecha en formato yymmdd (solo últimos 2 dígitos del año)
        const now = new Date();
        const fecha = String(now.getFullYear()).slice(-2) +
            String(now.getMonth() + 1).padStart(2, '0') +
            String(now.getDate()).padStart(2, '0');

        // Obtener hora minuto segundo inicial (hhmmss)
        const horaMinutoSegundo =
            String(now.getHours()).padStart(2, '0') +
            String(now.getMinutes()).padStart(2, '0') +
            String(now.getSeconds()).padStart(2, '0');

        const codigoSinPuntos = original.codigo.replace(/\./g, '');
        const nuevasSeries: string[] = [];

        const inicioSecuencia = Math.max(parseInt(horaMinutoSegundo), lastSequenceRef.current + 1);

        for (let i = 0; i < cantidad; i++) {
            const secuenciaNumero = inicioSecuencia + i;
            const secuencia = String(secuenciaNumero).padStart(6, '0');

            const codigoGenerado = `${letras}${fecha}${codigoSinPuntos}${secuencia}`;
            nuevasSeries.push(codigoGenerado);
        }

        lastSequenceRef.current = inicioSecuencia + cantidad - 1;
        setSeriesGeneradas(nuevasSeries);
    };

    const columnsSeries = useMemo<MRT_ColumnDef<{ serie: string }>[]>(
        () => [
            {
                accessorKey: "serie",
                header: "SERIE GENERADA",
                size: 100,
            },
        ],
        []
    );

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {/* Título principal */}
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        ✏️ Generador de Series
                    </Typography>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Lista de Productos" id="tab-0" aria-controls="tabpanel-0" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3} justifyContent={'start'}>
                        <Grid item xs={8}>
                            <GeneralTable<IDataProducto>
                                columns={columns}
                                data={dataProductos?.data?.data || []}
                                //
                                isLoading={isLoadingProductos}
                                isRefetching={isRefetching}
                                // // search
                                enableGlobalFilter={false}
                                // // pagination
                                pagination={pagination}
                                onPaging={setPagination}
                                rowCount={dataProductos?.data?.count}
                                // // actions
                                enableColumnActions={false}
                                enableEditing={false}
                                canEdit={false}
                                enableManualFiltering={true}
                                columnFilters={columnFilters}
                                onColumnFiltersChange={setColumnFilters}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent={'flex-end'}>
                                    <Tooltip title="Click">
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            onClick={() => exportExcel(mapSeriesToExcel(seriesGeneradas.map(serie => ({ serie }))), 'series')}
                                            startIcon={
                                                <RiFileExcel2Fill />
                                            }
                                        >
                                            {'Exportar a Excel'}
                                        </Button>
                                    </Tooltip>
                                </Grid>

                                <GeneralTable<{ serie: string }>
                                    columns={columnsSeries}
                                    data={seriesGeneradas.map(serie => ({ serie }))}
                                    // isLoading={isLoading}
                                    pagination={paginationSeries}
                                    onPaging={setPaginationSeries}
                                    rowCount={seriesGeneradas.length}
                                    enableColumnActions={false}
                                    enableEditing={false}
                                    enableGlobalFilter={false}
                                    enableColumnFilters={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </TabPanel>
            </Box>
        </>
    );
}
